import { z } from 'zod';
import {worldFetch} from './world-transport';
import type { AuthClient } from './auth';
import type { Connection } from './network';
import { assetVersionSchema, librarySchema, sha256, type AssetVersion } from './asset-contract';
import { resultSchema } from './content';
import { prepareEnvironment, type PreparedEnvironment } from './environment-package';

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
const stageSchema = z.object({ id: z.string(), state: z.string(), versionId: z.string() });
interface Checkpoint { hash: string; license: string; batch: string; install?: unknown }
class ImportError extends Error { constructor(readonly code: string, detail?: string) { super(messages[code] ?? detail ?? code.replaceAll('_', ' ')); } }
const messages: Record<string, string> = {
  object_leased: 'Finish active object edits, then retry the import.', resource_busy: 'The room is saving another change. Retry the import.',
  environment_budget: 'Package exceeds the room environment budget. Rebuild with smaller textures or fewer draws/triangles.',
  environment_hash_mismatch: 'A published asset does not match the package checksum.',
  forbidden: 'Only the room owner or an administrator can install its environment.',
  stale_epoch: 'The room session changed. Retry after reconnecting.',
  conflict: 'The room changed during import. Retry to install against its current revision.',
};

export class EnvironmentImporter {
  busy = false;
  private generation = 0;
  private controller: AbortController | null = null;
  private targetRoom = '';
  private installing = false;
  private stageID = '';
  constructor(private connection: Connection, private auth: AuthClient, private stopInput: () => void) {
    el('import-environment').addEventListener('click', () => void this.import());
    el('cancel-environment-import').addEventListener('click', () => { if (!this.installing) this.controller?.abort(); });
    this.render();
  }
  private message(text: string): void { el('environment-status').textContent = text; }
  private key(room = this.connection.roomID): string { return `offworld.environment-import.v1.${this.auth.state?.worldId}.${this.auth.state?.user?.id}.${room}`; }
  private save(key: string, checkpoint: Checkpoint | null): void { try { if (checkpoint) sessionStorage.setItem(key, JSON.stringify(checkpoint)); else sessionStorage.removeItem(key); } catch { /* Optional retry persistence. */ } }
  private read(key: string): Checkpoint | null {
    try { return z.object({ hash: z.string(), license: z.string(), batch: z.string(), install: z.unknown().optional() }).parse(JSON.parse(sessionStorage.getItem(key) ?? 'null')); } catch { return null; }
  }
  private async request(path: string, body?: unknown, method = 'POST', bytes?: Uint8Array): Promise<unknown> {
    const response = await worldFetch(path, { method, headers: { 'Content-Type': bytes ? 'application/octet-stream' : 'application/json', 'X-Offworld-CSRF': this.auth.state?.csrf ?? '', 'X-Offworld-Epoch': this.connection.state.epoch },
      body: bytes ? bytes as Uint8Array<ArrayBuffer> : body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.any([this.controller!.signal, AbortSignal.timeout(bytes ? 35000 : 7000)]),
    });
    const result: unknown = await response.json();
    if (!response.ok) {
      const receipt = resultSchema.safeParse(result); if (receipt.success) throw new ImportError(receipt.data.receipt.status);
      const detail = typeof result === 'object' && result !== null && 'detail' in result && typeof result.detail === 'string' ? result.detail : undefined;
      throw new ImportError(typeof result === 'object' && result !== null && 'error' in result ? String(result.error) : 'request_failed', detail);
    }
    return result;
  }
  private async library(): Promise<AssetVersion[]> {
    const versions: AssetVersion[] = []; let offset = 0;
    for (let page = 0; page < 64; page++) {
      const value = librarySchema.parse(await this.request(`/api/assets?offset=${offset}`, undefined, 'GET')); versions.push(...value.versions);
      if (value.next < 0) return versions;
      if (value.next <= offset) throw new Error('Invalid asset library pagination'); offset = value.next;
    }
    throw new Error('Asset library is too large to scan.');
  }
  private async publish(pkg: PreparedEnvironment, checkpoint: Checkpoint, guard: () => void): Promise<AssetVersion[]> {
    const available = await this.library(), versions: AssetVersion[] = [];
    for (const [index, asset] of pkg.manifest.assets.entries()) {
      guard(); this.message(`Publishing ${index + 1}/${pkg.manifest.assets.length}: ${asset.name}`);
      const existing = available.find(v => v.kind === 'prop' && v.visibility !== 'private' && !v.retiredAt && v.hash === asset.sha256 && v.bytes === asset.bytes);
      if (existing) { versions.push(existing); continue; }
      const requestId = (await sha256(new TextEncoder().encode(`${checkpoint.batch}:${asset.key}`))).slice(0, 32);
      const stage = stageSchema.parse(await this.request('/api/assets/uploads', {
        requestId, familyId: requestId, expectedRevision: 0, kind: 'prop', name: asset.name, visibility: 'world',
        license: checkpoint.license, source: `Environment package ${pkg.manifest.name} (${pkg.hash})`, hash: asset.sha256, bytes: asset.bytes,
      }));
      guard();
      if (stage.state === 'rejected' || stage.state === 'cancelled') throw new ImportError('upload_unavailable');
      let version: AssetVersion;
      if (stage.state === 'published') version = assetVersionSchema.parse(await this.request(`/api/assets/versions/${stage.versionId}`, undefined, 'GET'));
      else {
        this.stageID = stage.id;
        const bytes = new Uint8Array(await pkg.files.get(asset.key)!.arrayBuffer()); guard();
        const result = z.object({ version: assetVersionSchema }).parse(await this.request(`/api/assets/uploads/${stage.id}/data`, undefined, 'PUT', bytes));
        version = result.version; this.stageID = '';
      }
      if (version.hash !== asset.sha256 || version.bytes !== asset.bytes || version.kind !== 'prop' || version.visibility === 'private') throw new ImportError('environment_hash_mismatch');
      if (version.retiredAt) throw new ImportError('asset_retired');
      versions.push(version); available.push(version);
    }
    return versions;
  }

  private async import(): Promise<void> {
    const room = this.connection.state.content?.room;
    if (this.busy || !room || !this.connection.state.permissions.canManage) return;
    const files = Array.from(el<HTMLInputElement>('environment-package').files ?? []);
    if (!files.length) { this.message('Choose the pipeline output folder first.'); return; }
    if ([...this.connection.edits.values()].some(edit => edit.expiresMs > this.connection.serverTime())) { this.message(messages.object_leased!); return; }
    const generation = ++this.generation, key = this.key(room.id); this.targetRoom = room.id;
    this.busy = true; this.installing = false; this.controller = new AbortController(); this.stopInput(); this.render();
    const guard = (): void => { this.controller?.signal.throwIfAborted(); if (generation !== this.generation || this.connection.roomID !== room.id || !this.auth.state?.user) throw new Error('Import session changed'); };
    let checkpoint: Checkpoint | null = null, preflightComplete = false;
    try {
      const pkg = await prepareEnvironment(files, text => { guard(); this.message(text); }, this.controller.signal); guard(); preflightComplete = true;
      if (this.connection.state.content?.room.environmentData?.packageHash === pkg.hash) { this.save(key, null); this.message(`${pkg.manifest.name} is already installed.`); return; }
      const saved = this.read(key), license = el<HTMLInputElement>('environment-license').value.trim() || 'Unspecified';
      if ([...license].length > 80 || /[\r\n\0]/.test(license)) throw new Error('License / attribution must be at most 80 characters on one line.');
      checkpoint = saved?.hash === pkg.hash ? saved : { hash: pkg.hash, license, batch: crypto.randomUUID() };
      el<HTMLInputElement>('environment-license').value = checkpoint.license; this.save(key, checkpoint);
      const versions = await this.publish(pkg, checkpoint, guard); guard();
      if (!this.connection.state.epoch || !this.connection.state.content) throw new Error('Reconnect to the room, then retry. Published chunks will be reused.');
      if (!checkpoint.install) checkpoint.install = {
        requestId: crypto.randomUUID(), kind: 'environment.install', expectedRevision: this.connection.state.content.room.revision,
        environment: { schema: 1, name: pkg.manifest.name, packageHash: pkg.hash, map: pkg.map, ...(pkg.manifest.lighting ? { lighting: pkg.manifest.lighting } : {}),
          assets: pkg.manifest.assets.map((asset, index) => ({ key: asset.key, versionId: versions[index]!.id, hash: asset.sha256, bytes: asset.bytes })), placements: pkg.manifest.placements },
      };
      this.save(key, checkpoint); this.installing = true; this.message('Installing environment, collision, and spawn…'); this.render();
      const result = resultSchema.parse(await this.request(`/api/rooms/${room.id}/environment`, checkpoint.install)); guard();
      if (result.receipt.status !== 'applied') throw new ImportError(result.receipt.status);
      this.save(key, null);
      this.message(result.replayed && this.connection.state.content?.room.environmentData?.packageHash !== pkg.hash
        ? 'Previous import confirmed; the room has changed since. Import again to replace its current environment.'
        : `Imported ${pkg.manifest.name} · ${pkg.manifest.assets.length} parts · ${pkg.map.obstacles.length} collision proxies`);
    } catch (error) {
      if (generation !== this.generation) return;
      if (!preflightComplete) el<HTMLInputElement>('environment-package').value = '';
      if (checkpoint && this.connection.state.content?.room.environmentData?.packageHash === checkpoint.hash) { this.save(key, null); this.message('Environment installed.'); }
      else if (this.controller?.signal.aborted) {
        this.save(key, null); this.message('Import stopped. The room environment was not changed.');
        if (this.stageID) void this.auth.command(`/api/assets/uploads/${this.stageID}/cancel`, {}).catch(() => {});
      } else {
        if (checkpoint && error instanceof ImportError && !error.code.startsWith('storage_') && !['outcome_unknown', 'server_closing', 'room_unavailable', 'room_overloaded', 'api_overloaded', 'resource_busy'].includes(error.code)) {
          delete checkpoint.install;
          if (['upload_unavailable', 'asset_retired'].includes(error.code)) checkpoint.batch = crypto.randomUUID();
          this.save(key, checkpoint);
        }
        this.message(error instanceof z.ZodError ? `Invalid package: ${error.issues[0]?.message}` : `${error instanceof Error ? error.message : 'Import failed'}${this.installing ? ' — retry the same folder to check or complete the import.' : ''}`);
      }
    } finally {
      if (generation === this.generation) { this.busy = false; this.installing = false; this.controller = null; this.stageID = ''; this.render(); }
    }
  }
  authChanged(): void {
    this.generation++; this.controller?.abort(); this.controller = null; this.busy = false; this.installing = false; this.stageID = '';
    el<HTMLInputElement>('environment-package').value = ''; this.message(''); this.render();
  }
  render(): void {
    if (this.busy && this.connection.roomID !== this.targetRoom && !this.installing) this.controller?.abort();
    const environment = this.connection.state.content?.room.environmentData;
    el('environment-current').textContent = environment ? `${environment.name} · ${environment.objectIds.length} parts` : 'Prototype environment';
    const enabled = !!this.auth.state?.user && this.connection.state.permissions.canManage;
    for (const id of ['environment-package', 'environment-license', 'import-environment']) el<HTMLInputElement>(id).disabled = this.busy || !enabled;
    el<HTMLButtonElement>('cancel-environment-import').hidden = !this.busy;
    el<HTMLButtonElement>('cancel-environment-import').disabled = this.installing;
  }
  dispose(): void { this.generation++; this.controller?.abort(); }
}
